import React, { useContext, useMemo, memo } from "react";
import { Box, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { TOContext } from "context/TContext";
import useStripePay from "hooks/useStripePay";
import { useCreditCard } from "hooks/useCreditCard";
import TOCardPayment from "components/Payments/TOCardPayment";
import PaymentDivider from "components/Shared/PaymentDivider";
import { useTipInfoSettingsContext } from "context/TipInfoSettingsContext";
import AppleGooglePayButtonStripe from "components/CreditCardForm/StripeForm/AppleGooglePayButtonStripe";
import {
  PaymentMethodIcons,
  CashPaymentSection,
  PaymentHeaderSection,
  PaymentTypeHeader,
  useCommonVisibilityConditions,
  useCommonHandlers,
} from "./shared/PaymentSharedComponents";

// Memoized Stripe Apple/Google Pay section component
const StripeAppleGooglePaySection = memo(
  ({ paymentRequest, handlePaymentRequest, showAppleGooglePay }) => {
    if (!paymentRequest || !showAppleGooglePay) return null;

    return (
      <Grid item xs={12}>
        <AppleGooglePayButtonStripe
          handlePaymentRequest={handlePaymentRequest}
          paymentRequest={paymentRequest}
        />
        <PaymentDivider />
      </Grid>
    );
  },
);

StripeAppleGooglePaySection.displayName = "StripeAppleGooglePaySection";

// Stripe Payments component
const StripePayments = memo(({ loaded, error, bId, oId, isWebView }) => {
  const tipInfoSettings = useTipInfoSettingsContext();
  const { isDriver, orderType, business } = useContext(TOContext);

  // Backend may send 0/1 or false/true. Any truthy value = "button hidden".
  const isApplePayDisabledForBusiness = Boolean(
    business?.settings?.disableApplePay,
  );
  const isGooglePayDisabledForBusiness = Boolean(
    business?.settings?.disableGooglePay,
  );

  const {
    userInfo,
    errorCard,
    setErrorCard,
    tokenForward,
    setHasTokenForward,
    handleSubmit,
    handleCashSubmit,
    loadingPay,
  } = useCreditCard();

  const {
    handleSubmitStripe,
    isStripeApplePaySupported,
    isStripeGooglePaySupported,
    paymentRequest,
    isLoading,
  } = useStripePay();

  // Use shared common visibility conditions
  const commonVisibilityConditions = useCommonVisibilityConditions(
    loaded,
    error,
    isDriver,
    tipInfoSettings,
    orderType,
  );

  // Memoize Stripe-specific visibility conditions with settings checks
  const visibilityConditions = useMemo(
    () => ({
      ...commonVisibilityConditions,
      // Show the button when at least one device-supported payment method
      // is also enabled in settings. Each method is checked independently
      // so that disabling Apple Pay does NOT suppress Google Pay and vice-versa.
      showAppleGooglePay:
        paymentRequest &&
        ((isStripeGooglePaySupported && !isGooglePayDisabledForBusiness) ||
          (isStripeApplePaySupported && !isApplePayDisabledForBusiness)),
    }),
    [
      commonVisibilityConditions,
      paymentRequest,
      isStripeGooglePaySupported,
      isStripeApplePaySupported,
      isGooglePayDisabledForBusiness,
      isApplePayDisabledForBusiness,
    ],
  );

  // Use shared common handlers
  const commonHandlers = useCommonHandlers(
    handleSubmit,
    handleCashSubmit,
    setErrorCard,
    setHasTokenForward,
  );

  // Memoize handlers with Stripe-specific additions
  const memoizedHandlers = useMemo(
    () => ({
      ...commonHandlers,
      handleSubmitStripe,
    }),
    [commonHandlers, handleSubmitStripe],
  );

  // Memoize credit card props for Stripe payments
  const creditCardProps = useMemo(
    () => ({
      isWebView,
      userInfo,
      loadingPay,
      errorCard,
      setErrorCard: memoizedHandlers.setErrorCard,
      tokenForward,
      setHasTokenForward: memoizedHandlers.setHasTokenForward,
      handleSubmit: memoizedHandlers.handleSubmit,
      handleSubmitStripe: memoizedHandlers.handleSubmitStripe,
      isStripeApplePaySupported,
      isStripeGooglePaySupported,
      paymentRequest,
      isLoading,
    }),
    [
      isWebView,
      userInfo,
      loadingPay,
      errorCard,
      tokenForward,
      memoizedHandlers,
      isStripeApplePaySupported,
      isStripeGooglePaySupported,
      paymentRequest,
      isLoading,
    ],
  );

  const handlePaymentRequest = () => {
    if (!paymentRequest) {
      console.error("Payment request not available");
      return;
    }

    try {
      paymentRequest?.show();
      handleSubmitStripe({}, true); // Pass empty object for values and true for isGooglePayOrApplePay
    } catch (error) {
      console.error("Error showing payment request:", error);
      toast.error("Failed to show payment method. Please try again.");
    }
  };

  return (
    <>
      <PaymentHeaderSection
        isWebView={isWebView}
        bId={bId}
        oId={oId}
        showKitchenTips={visibilityConditions.showKitchenTips}
        showDriverTips={visibilityConditions.showDriverTips}
      />

      <Box sx={{ mt: isWebView ? 0 : 2 }}>
        <Grid container spacing={1}>
          <PaymentTypeHeader isWebView={isWebView} />

          <Grid item xs={12}>
            <PaymentMethodIcons />
          </Grid>

          <StripeAppleGooglePaySection
            paymentRequest={
              (isStripeApplePaySupported && !isApplePayDisabledForBusiness) ||
              (isStripeGooglePaySupported && !isGooglePayDisabledForBusiness)
                ? paymentRequest
                : null
            }
            handlePaymentRequest={handlePaymentRequest}
            showAppleGooglePay={visibilityConditions.showAppleGooglePay}
          />

          <CashPaymentSection
            showDriverContent={visibilityConditions.showDriverContent}
            loadingPay={loadingPay}
            handleCashSubmit={memoizedHandlers.handleCashSubmit}
            oId={oId}
            showCashPayment={visibilityConditions.showCashPayment}
          />

          <Grid item xs={12}>
            {visibilityConditions.showCardPayment && (
              <TOCardPayment {...creditCardProps} />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
});

StripePayments.displayName = "StripePayments";

export default StripePayments;
